import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { api, ApiError } from "../lib/api.ts";

/**
 * Modal form to create a new :Business node in the graph.
 *
 * Tier and mode aren't editable after creation (PATCH only covers name, stage,
 * main_risk, notes — see businesses route), so we expose them here. Financials
 * are optional and can be added later via the business profile once we expose
 * them in PATCH too.
 */
export function NewBusinessModal({ onClose }: { onClose: () => void }) {
	const navigate = useNavigate();
	const [id, setId] = useState("");
	const [name, setName] = useState("");
	const [tier, setTier] = useState<"A0" | "A1" | "A2" | "B" | "venture-bet" | "out">("B");
	const [mode, setMode] = useState("operating");
	const [stage, setStage] = useState("ideation");
	const [mainRisk, setMainRisk] = useState("");
	const [notes, setNotes] = useState("");
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);

	function suggestId(value: string) {
		// Convert display name to slug suggestion on first keystroke if id is empty.
		const slug = value
			.toLowerCase()
			.replace(/[^a-z0-9]+/g, "-")
			.replace(/^-|-$/g, "")
			.slice(0, 64);
		if (id === "") setId(slug);
		setName(value);
	}

	async function handleCreate() {
		setBusy(true);
		setError(null);
		try {
			const result = await api.createBusiness({
				id,
				name,
				tier,
				mode,
				stage,
				main_risk: mainRisk.trim() === "" ? null : mainRisk,
				notes: notes.trim() === "" ? null : notes,
			});
			navigate(`/businesses/${result.business.id}`);
		} catch (err) {
			if (err instanceof ApiError && err.status === 409) {
				setError(`Business id "${id}" already exists.`);
			} else {
				setError((err as Error).message);
			}
			setBusy(false);
		}
	}

	const canSubmit = id.length >= 2 && name.length >= 1 && !busy;

	return (
		<div
			className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
			onClick={onClose}
		>
			<div
				className="w-full max-w-lg border border-zinc-800 rounded-lg bg-terminal shadow-xl"
				onClick={(e) => e.stopPropagation()}
			>
				<header className="px-4 py-3 border-b border-zinc-800 flex items-baseline justify-between">
					<h2 className="text-base font-semibold">New business</h2>
					<button
						type="button"
						onClick={onClose}
						className="text-xs text-zinc-500 hover:text-zinc-300"
					>
						close
					</button>
				</header>
				<div className="p-4 space-y-3">
					<div>
						<label className="text-xs text-zinc-500">Name</label>
						<input
							type="text"
							value={name}
							onChange={(e) => suggestId(e.target.value)}
							placeholder="G1 Marketing"
							autoFocus
							className="mt-1 w-full px-3 py-1.5 bg-zinc-900 border border-zinc-700 rounded text-zinc-100 text-sm placeholder:text-zinc-600 focus:outline-none focus:border-zinc-500"
							disabled={busy}
						/>
					</div>
					<div>
						<label className="text-xs text-zinc-500">
							Slug (id, used in URLs and the graph — immutable)
						</label>
						<input
							type="text"
							value={id}
							onChange={(e) => setId(e.target.value.toLowerCase())}
							placeholder="g1-marketing"
							className="mt-1 w-full px-3 py-1.5 bg-zinc-900 border border-zinc-700 rounded text-zinc-100 text-sm font-mono placeholder:text-zinc-600 focus:outline-none focus:border-zinc-500"
							disabled={busy}
						/>
						<div className="text-[11px] text-zinc-600 mt-1">
							Lowercase, digits, hyphens. No leading/trailing hyphen.
						</div>
					</div>
					<div className="grid grid-cols-2 gap-3">
						<div>
							<label className="text-xs text-zinc-500">Tier</label>
							<select
								value={tier}
								onChange={(e) => setTier(e.target.value as typeof tier)}
								className="mt-1 w-full px-3 py-1.5 bg-zinc-900 border border-zinc-700 rounded text-zinc-100 text-sm focus:outline-none focus:border-zinc-500"
								disabled={busy}
							>
								<option value="A0">A0 — board</option>
								<option value="A1">A1 — operating</option>
								<option value="A2">A2 — operating</option>
								<option value="B">B — operating</option>
								<option value="venture-bet">venture-bet</option>
								<option value="out">out — out of taxonomy</option>
							</select>
						</div>
						<div>
							<label className="text-xs text-zinc-500">Mode</label>
							<input
								type="text"
								value={mode}
								onChange={(e) => setMode(e.target.value)}
								className="mt-1 w-full px-3 py-1.5 bg-zinc-900 border border-zinc-700 rounded text-zinc-100 text-sm focus:outline-none focus:border-zinc-500"
								disabled={busy}
							/>
						</div>
					</div>
					<div>
						<label className="text-xs text-zinc-500">Stage</label>
						<input
							type="text"
							value={stage}
							onChange={(e) => setStage(e.target.value)}
							className="mt-1 w-full px-3 py-1.5 bg-zinc-900 border border-zinc-700 rounded text-zinc-100 text-sm focus:outline-none focus:border-zinc-500"
							disabled={busy}
						/>
					</div>
					<div>
						<label className="text-xs text-zinc-500">Main risk (optional)</label>
						<textarea
							value={mainRisk}
							onChange={(e) => setMainRisk(e.target.value)}
							rows={2}
							className="mt-1 w-full px-3 py-1.5 bg-zinc-900 border border-zinc-700 rounded text-zinc-200 text-sm resize-y focus:outline-none focus:border-zinc-500"
							disabled={busy}
						/>
					</div>
					<div>
						<label className="text-xs text-zinc-500">Notes (optional)</label>
						<textarea
							value={notes}
							onChange={(e) => setNotes(e.target.value)}
							rows={2}
							className="mt-1 w-full px-3 py-1.5 bg-zinc-900 border border-zinc-700 rounded text-zinc-300 text-sm resize-y focus:outline-none focus:border-zinc-500"
							disabled={busy}
						/>
					</div>
					{error && <div className="text-xs text-red-300">{error}</div>}
				</div>
				<footer className="px-4 py-3 border-t border-zinc-800 flex justify-end gap-2">
					<button
						type="button"
						onClick={onClose}
						disabled={busy}
						className="px-3 py-1.5 border border-zinc-700 hover:border-zinc-600 text-zinc-300 text-sm rounded"
					>
						Cancel
					</button>
					<button
						type="button"
						onClick={handleCreate}
						disabled={!canSubmit}
						className="px-3 py-1.5 bg-accent-600 hover:bg-accent-500 disabled:bg-zinc-800 disabled:text-zinc-500 text-white text-sm font-medium rounded"
					>
						{busy ? "Creating…" : "Create"}
					</button>
				</footer>
			</div>
		</div>
	);
}
